/**
 * Deployment environment label — the single source of truth for *which*
 * environment a piece of telemetry belongs to.
 *
 * This is intentionally decoupled from `NODE_ENV`. Both the production and the
 * staging (dev) deployments build and run with `NODE_ENV=production`, so
 * `NODE_ENV` alone cannot tell PostHog which environment a log / error / event
 * came from — they'd all collapse into "production" and become impossible to
 * separate in the PostHog UI.
 *
 * Each deployment sets an explicit env var instead:
 *
 *   • Production deployment    → APP_ENV=production   NEXT_PUBLIC_APP_ENV=production
 *   • Staging (dev) deployment → APP_ENV=staging      NEXT_PUBLIC_APP_ENV=staging
 *   • Local dev / tests        → unset → resolves to 'development'
 *
 * `APP_ENV` is read on the server / worker (Vercel + Railway). The browser
 * bundle only ever sees `NEXT_PUBLIC_APP_ENV` because Next.js inlines
 * `NEXT_PUBLIC_*` vars at build time and replaces other `process.env.*`
 * references with `undefined` in client code — so this helper checks both and
 * stays usable from every runtime (server, worker, browser).
 *
 * Every telemetry surface stamps this value so a single shared project can be
 * filtered by `environment`:
 *   - Sentry errors / logs / metrics / traces — `environment` init option
 *     (`sentry-server.ts`, `instrumentation-client.ts`)
 *   - PostHog product events — `environment` property (`posthog-server.ts`)
 *   - PostHog browser analytics — `environment` super property (`analytics/client.ts`)
 */

export type DeploymentEnvironment = 'production' | 'staging' | 'development';

export function getDeploymentEnvironment(): DeploymentEnvironment {
  const explicit = process.env.APP_ENV ?? process.env.NEXT_PUBLIC_APP_ENV;
  if (
    explicit === 'production' ||
    explicit === 'staging' ||
    explicit === 'development'
  ) {
    return explicit;
  }
  // No explicit label: a production build with the var missing is treated as
  // 'production' (safe default for a real deploy that forgot to set it);
  // everything else (dev server, vitest, playwright) is 'development'.
  return process.env.NODE_ENV === 'production' ? 'production' : 'development';
}

/**
 * Extracts the Supabase project ref (the subdomain — e.g. `frjlotqnaryhdqvprhcn`
 * for the Dev project, `gcsqcfkimcasqnfsevdp` for Prod) from
 * `NEXT_PUBLIC_SUPABASE_URL`.
 *
 * Used for diagnostics ONLY — it lets a worker log *which* Supabase project it
 * is actually talking to so a staging/production DB mismatch is obvious at a
 * glance. It deliberately returns only the ref (never the key, never the full
 * URL with credentials) so it is safe to ship to logs/telemetry.
 *
 * Returns `'unknown'` when the URL is unset or unparseable.
 */
export function getSupabaseProjectRef(): string {
  const url = process.env.NEXT_PUBLIC_SUPABASE_URL;
  if (!url) return 'unknown';
  try {
    // Hostname looks like `<ref>.supabase.co` (or `<ref>.supabase.in`, etc.).
    const host = new URL(url).hostname;
    const ref = host.split('.')[0];
    return ref || 'unknown';
  } catch {
    return 'unknown';
  }
}
